import type { ReactNode } from 'react';
import { useVideoPlayerContext } from '@/components/VideoPlayer/VideoPlayerProvider';
import { SkipIndicator } from '@/components/VideoPlayer/SkipIndicator';
import { VideoBufferingOverlay } from '@/components/VideoPlayer/VideoBufferingOverlay';
import { useVideoLoopPlayback } from '@/components/VideoPlayer/hooks/useVideoLoopPlayback';
import { useVideoBuffering } from '@/components/VideoPlayer/hooks/useVideoBuffering';
import type { FullSource } from '@/types/source';
import { usePipEscapeClose } from '../hooks/usePipEscapeClose';
import { usePipPlayerKeyboardShortcuts } from '../hooks/usePipPlayerKeyboardShortcuts';
import { usePipVideoAspect } from '../hooks/usePipVideoAspect';
import { PipPlayerTopBar } from './PipPlayerTopBar';
import { PipPlayerCenterControls } from './PipPlayerCenterControls';
import { PipPlayerBottomBar } from './PipPlayerBottomBar';
import { PipVideoContent } from './PipVideoContent';

interface PipPlayerShellProps {
    source: FullSource;
    overlay?: ReactNode;
}

export function PipPlayerShell({ source, overlay }: PipPlayerShellProps) {
    const { containerRef, streaming, skipIndicator, togglePlay, onSkip } =
        useVideoPlayerContext();
    const isBuffering = useVideoBuffering();
    usePipEscapeClose();
    useVideoLoopPlayback();
    usePipVideoAspect();
    usePipPlayerKeyboardShortcuts({
        live: streaming.live,
        togglePlay,
        onSkip,
    });

    return (
        <div
            ref={containerRef}
            className="group/pip relative w-full h-full overflow-hidden rounded-[2px] bg-black-100 cursor-pointer"
            onClick={togglePlay}
        >
            <div className="absolute inset-0">
                <PipVideoContent source={source} />
            </div>

            {overlay}

            {isBuffering && <VideoBufferingOverlay />}

            <PipPlayerTopBar sourceName={source.displayName} />
            <PipPlayerCenterControls />
            <PipPlayerBottomBar />

            {skipIndicator.isVisible && skipIndicator.direction && (
                <SkipIndicator
                    direction={skipIndicator.direction}
                    accumulatedSeconds={skipIndicator.accumulatedSeconds}
                    isVisible={skipIndicator.isVisible}
                />
            )}
        </div>
    );
}
